import { useState, useCallback } from 'react';
import ToastMsg from "../../components/ToastMsg";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import { Navigate, useNavigate} from "react-router-dom";
import EmailInputField from '../../components/EmailInputField';
import PasswordInputField from '../../components/PasswordInputField';
import NavigationPanel from "../../components/NavigationPanel";
import Breadcrumb from '../../components/Breadcrumb';
import CTAs from '../../components/CTAs';
import NameInput from "../../components/NameInput";
import Footer from '../../components/Footer.js';
import SettingNav from '../../components/settingNav.js';

const SettingsGeneralPage = () => {
	const [name, setName] = useState('');
    const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
  	const [isToastMsgOpen, setToastMsgOpen] = useState(false);
  	const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  	const navigate = useNavigate();
  	
  	const openToastMsg = useCallback(() => {
    		setToastMsgOpen(true);
  	}, []);
  	
  	const closeToastMsg = useCallback(() => {
    		setToastMsgOpen(false);
  	}, []);
  	
  	const closeLogoutConfirmation = useCallback(() => {
    		setLogoutConfirmationOpen(false);
  	}, []);

	const breadcrumb = [
		{ label: "", path: "/postlba", icon: "assets/home.svg", active: true },
		{ label: "Settings", path: "", icon: "", active: false },
		{ label: "General", path: "", icon: "", active: true },
	];

	const [isDarkMode, setIsDarkMode] = useState(false);

	const navItems = [
		{
			text: 'Debt collection', 
    		bgColor: 'bg-primary-primary-50', 
    		textColor: 'text-neutrals-neutral-900', 
    		hoverBgColor: 'bg-primary-primary-50', 
    		hoverTextColor: 'text-neutrals-neutral-900', 
    		path: "",
    		icon: 'assets/arrow-down.svg'
		},

		{
			text: "Outcomes & costs",
			bgColor: 'bg-neutrals-neutral-50', 
    		textColor: 'text-neutrals-neutral-300', 
    		hoverBgColor: 'bg-neutrals-neutral-50', 
    		hoverTextColor: 'text-primary-primary-500', 
    		path: "/outcome&cost",
    		icon: ''
		},

		{
			text: "AI indicators",
			bgColor: 'bg-neutrals-neutral-50', 
    		textColor: 'text-neutrals-neutral-300', 
    		hoverBgColor: 'bg-neutrals-neutral-50', 
    		hoverTextColor: 'text-primary-primary-500', 
    		path: "/ai1",
    		icon: ''
		},
	];
  	
  	return (<>
    		<div className="w-full relative bg-neutrals-neutral-50 min-h-screen overflow-visible text-left text-sm text-neutrals-neutral-900 font-open-sans-ff-body-strong">
				{/* Navigation Panel */}
				<NavigationPanel navItems={navItems}/>

				{/* Main */}
				<div className='w-full absolute top-[128px] flex flex-col items-start justify-start gap-6 px-4 md:px-4 lg:px-24 box-border'>
					{/* Breadcrumb */}
					<Breadcrumb breadcrumbs={breadcrumb}/>

					<div className='self-stretch flex flex-col items-start justify-start gap-6 text-9xl text-Primary-primary-900'>
						{/* Heading */}
						<div className="self-stretch bg-white border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-col items-start justify-start pt-0 px-0 pb-6 gap-1.5">
							<b className="self-stretch relative">Settings</b>
							<div className="self-stretch relative text-base text-neutrals-neutral-300">Get your billing information, generate/review invoices.</div>
						</div>

						<div className='self-stretch flex flex-row items-start justify-start gap-8 text-base text-neutrals-neutral-300'>
							{/* Tabs */}
							<SettingNav/>

							{/* Right side */}
							<div className='w-full flex-1 self-stretch flex flex-col items-start justify-start gap-6 text-lg text-neutrals-neutral-900'>
								<div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-start justify-between pt-0 px-0 pb-6 gap-0">
									<div className="flex-1 flex flex-col items-start justify-start gap-1.5">
										<div className="relative font-semibold">General</div>
										<div className="relative text-sm leading-[20px] font-inter text-neutrals-neutral-300">Update personal details here.</div>
									</div>
									<div className="flex flex-row items-start justify-end gap-3 text-sm">
										<CTAs
										type="Ghostbtn"
										text="Cancel"
										textColor="text-neutrals-neutral-900"
										bgColor="bg-neutrals-neutral-50"
										borderColor="border-neutrals-neutral-100"
										position="relative"
										/>
          					
										<CTAs
										type="Primarybtn"
										text="Save"
										textColor="text-neutrals-neutral-50"
										bgColor="bg-primary-primary-500"
										borderColor="border-primary-primary-500"
										position="relative"
										onClick={openToastMsg}
										/>
									</div>
								</div>

								<div className='self-stretch flex flex-col items-start justify-start gap-8'>
									{/* Profile info */}
									<div className="self-stretch flex flex-col items-start justify-start gap-6">
										<div className="self-stretch relative font-semibold">Profile information</div>
										<div className="self-stretch flex flex-col items-start justify-start gap-6 text-sm">
											<NameInput
											title='Full name'
											placeholder='John Clark'
											value={name}
											onChange={(e) => setName(e.target.value)}
											/>
											<EmailInputField
											title='Email address'
											value={email}
											bgColorLight='#FFFFFF'
											bgColorDark='#0C111D'
											placeholder='name@yourcompany.com'
											isDarkMode={isDarkMode}
											/>
										</div>
									</div>

									<div className='self-stretch flex flex-col items-start justify-start gap-6'>
										<div className="self-stretch relative font-semibold">Security settings</div>
										<div className="self-stretch flex flex-col items-start justify-start gap-6 text-sm">
											<div className="self-stretch flex flex-col items-start justify-start gap-2.5">
												<div className="flex flex-row items-center justify-start gap-1.5">
													<div className="relative font-semibold">Current password</div>
													<img className="w-5 relative h-5 overflow-hidden shrink-0" alt="" src="assets/help-circle.svg" />
												</div>
												<div className="w-[400px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border overflow-hidden flex flex-row items-center justify-start p-3 text-base text-neutrals-neutral-300">
													<div className="flex-1 relative">Enter your current password</div>
												</div>
											</div>
											<div className="self-stretch flex flex-col items-start justify-start gap-2.5">
												<div className="flex flex-row items-center justify-start gap-1.5">
													<div className="relative font-semibold">New password</div>
													<img className="w-5 relative h-5 overflow-hidden shrink-0" alt="" src="assets/help-circle.svg" />
												</div>
												<div className="w-[400px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border overflow-hidden flex flex-row items-center justify-start p-3 text-base text-neutrals-neutral-300">
													<div className="flex-1 relative">Enter your new password</div>
												</div>
											</div>
											<div className="self-stretch flex flex-col items-start justify-start gap-2.5">
												<div className="flex flex-row items-center justify-start gap-1.5">
													<div className="relative font-semibold">Confirm new password</div>
													<img className="w-5 relative h-5 overflow-hidden shrink-0" alt="" src="assets/help-circle.svg" />
												</div>
												<div className="w-[400px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border overflow-hidden flex flex-row items-center justify-start p-3 text-base text-neutrals-neutral-300">
													<div className="flex-1 relative">Confirm your new password</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<Footer/>
				</div>
      			
    		</div>
    		{isToastMsgOpen && (
      			<PortalPopup
        				overlayColor="rgba(113, 113, 113, 0.3)"
        				placement="Centered"
        				onOutsideClick={closeToastMsg}
        				>
        				<ToastMsg onClose={closeToastMsg} />
      			</PortalPopup>
    		)}
    		{isLogoutConfirmationOpen && (
      			<PortalPopup
        				overlayColor="rgba(113, 113, 113, 0.3)"
        				placement="Centered"
        				onOutsideClick={closeLogoutConfirmation}
        				>
        				<LogoutConfirmation onClose={closeLogoutConfirmation} />
      			</PortalPopup>
    		)}</>);
};

export default SettingsGeneralPage;